import React, { useState, useEffect } from "react";
import {
  X,
  Calendar,
  Hash,
  Save,
  AlertTriangle,
  Download,
  Upload,
  Eye,
  LogOut,
  Clipboard,
  Trash2,
  GraduationCap,
  BookOpen,
  History,
  Clock,
  RotateCcw,
  Monitor,
  Sun,
  Moon,
  Sparkles,
  Palette,
  FileText,
  Table2,
  Share2,
  BellRing,
} from "lucide-react";
import {
  fetchSnapshots,
  fetchSnapshot,
  restoreSnapshot,
  restoreSnapshotSection,
  formatTimestampFull,
  fetchSnapshotPrefs,
  saveSnapshotPrefs,
  saveSnapshotNow,
  clearAllSnapshots,
  localTimeZone,
} from "../services/api";
import PasskeySection from "./PasskeySection";
import AiSettingsSection from "./AiSettingsSection";
import SnapshotViewerModal from "./SnapshotViewerModal";
import { useBackdropClose } from "../utils/backdropClose";
import { useTheme } from "../theme/ThemeProvider";
import { ambientCapable } from "./AmbientBackground";

const SNAPSHOT_EVERY_OPTIONS = [1, 2, 3, 5, 7, 14, 30];
const SNAPSHOT_MAX_OPTIONS = [10, 20, 30, 50, 100];
const HOUR_LABEL = (h) => {
  const ampm = h < 12 ? "am" : "pm";
  const base = h % 12 === 0 ? 12 : h % 12;
  return `${base}:00 ${ampm}`;
};

const THEME_OPTIONS = [
  { id: "system", label: "System", Icon: Monitor },
  { id: "light", label: "Light", Icon: Sun },
  { id: "dark", label: "Dark", Icon: Moon },
];

const SettingsModal = ({
  isOpen,
  onClose,
  settings,
  onSave,
  onReset,
  onExport,
  onImport,
  onLogout,
  onDeleteAllClipboards,
  onCleanupDuplicates,
  clipboardCount = 0,
  showConferences = false,
  onToggleConferences,
  showPublications = false,
  onTogglePublications,
  cloudEnabled = false,
  onAfterRestore,
  onFlushPending,
  onAfterSectionRestore,
  onExportMarkdown,
  onExportCsv,
  onShareCard,
  streakReminderHour = 19,
  onStreakReminderHourChange,
  railSide = "left",
  onRailSideChange,
  onAiSettingsChanged,
}) => {
  const [localSettings, setLocalSettings] = useState(settings);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  // Version history (snapshots) state
  const [snapshots, setSnapshots] = useState([]);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  const [snapshotError, setSnapshotError] = useState(null);
  const [confirmRestoreId, setConfirmRestoreId] = useState(null);
  // A full restore from the list needs two confirmations (step 1, then 2).
  const [confirmRestoreStep, setConfirmRestoreStep] = useState(1);
  const [restoringId, setRestoringId] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);

  // Version-history schedule (every N days at a local hour, keep at most M).
  const [snapPrefs, setSnapPrefs] = useState(null);
  const [snapPrefsBusy, setSnapPrefsBusy] = useState(false);
  const [snapNotice, setSnapNotice] = useState(null);
  const [savingNow, setSavingNow] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Version preview ("View") state.
  const [viewOpen, setViewOpen] = useState(false);
  const [viewSnapshot, setViewSnapshot] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(null);
  const [viewRestoring, setViewRestoring] = useState(false);

  // Appearance (theme / skin / ambient) — applies instantly, saved locally and
  // mirrored to cloud settings by App.js.
  const { theme, skin, ambientEffects, setTheme, setSkin, setAmbientEffects, skins } =
    useTheme();
  const ambientAvailable = ambientCapable();

  // Update localSettings when settings prop changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalSettings({ ...settings });
    }
  }, [isOpen, settings]);

  // Load version history when the modal opens (cloud users only)
  const loadSnapshots = async () => {
    setLoadingSnapshots(true);
    setSnapshotError(null);
    try {
      const list = await fetchSnapshots();
      setSnapshots(Array.isArray(list) ? list : []);
    } catch (err) {
      setSnapshotError(err.message || "Failed to load version history");
    } finally {
      setLoadingSnapshots(false);
    }
  };

  const loadSnapPrefs = async () => {
    try {
      const p = await fetchSnapshotPrefs();
      setSnapPrefs(p);
      // First visit: pin the schedule to this browser's time zone so "2:00 am"
      // means 2:00 am here (the server defaults to UTC until it knows).
      const { tz, tzOffsetMinutes } = localTimeZone();
      if (p && (!p.exists || (tz && p.tz !== tz) || p.tzOffsetMinutes !== tzOffsetMinutes)) {
        await saveSnapshotPrefs({});
        setSnapPrefs({ ...p, tz, tzOffsetMinutes, exists: true });
      }
    } catch (err) {
      setSnapPrefs(null);
    }
  };

  const updateSnapPrefs = async (patch) => {
    if (!snapPrefs) return;
    const next = { ...snapPrefs, ...patch };
    setSnapPrefs(next);
    setSnapPrefsBusy(true);
    setSnapNotice(null);
    try {
      await saveSnapshotPrefs({
        everyDays: next.everyDays,
        hour: next.hour,
        max: next.max,
      });
      if (patch.max !== undefined) loadSnapshots();
    } catch (err) {
      setSnapNotice({ ok: false, text: err.message || "Could not save the schedule." });
    } finally {
      setSnapPrefsBusy(false);
    }
  };

  const handleSaveNow = async () => {
    setSavingNow(true);
    setSnapNotice(null);
    try {
      const r = await saveSnapshotNow();
      setSnapNotice(
        r && r.saved
          ? { ok: true, text: "Version saved." }
          : { ok: true, text: "Nothing new to save — the newest version already matches your data." },
      );
      await loadSnapshots();
    } catch (err) {
      setSnapNotice({ ok: false, text: err.message || "Could not save a version." });
    } finally {
      setSavingNow(false);
    }
  };

  const handleClearAll = async () => {
    setClearing(true);
    setSnapNotice(null);
    try {
      const r = await clearAllSnapshots();
      setConfirmClear(false);
      if (r && r.more) {
        // The server keeps deleting in the background; poll until empty.
        setSnapNotice({ ok: true, text: "Clearing versions…" });
        for (let i = 0; i < 40; i++) {
          await new Promise((res) => setTimeout(res, 1500));
          const list = await fetchSnapshots();
          setSnapshots(Array.isArray(list) ? list : []);
          if (!list || list.length === 0) break;
        }
        setSnapNotice({ ok: true, text: "All saved versions cleared." });
      } else {
        setSnapNotice({ ok: true, text: `Cleared ${r && r.removed ? r.removed : 0} saved version(s).` });
        await loadSnapshots();
      }
    } catch (err) {
      setSnapNotice({ ok: false, text: err.message || "Could not clear versions." });
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    if (isOpen && cloudEnabled) {
      setConfirmRestoreId(null);
      setRestoreResult(null);
      setViewOpen(false);
      setViewSnapshot(null);
      setSnapNotice(null);
      setConfirmClear(false);
      loadSnapshots();
      loadSnapPrefs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, cloudEnabled]);

  // Open the read-only preview for a version and lazily load its full data.
  const handleView = async (snapshotId) => {
    setViewOpen(true);
    setViewSnapshot(null);
    setViewError(null);
    setViewLoading(true);
    try {
      const snap = await fetchSnapshot(snapshotId);
      setViewSnapshot(snap);
    } catch (err) {
      setViewError(err.message || "Failed to load this version.");
    } finally {
      setViewLoading(false);
    }
  };

  // Restore from inside the preview (same safe path as the list's Restore).
  const handleRestoreFromView = async () => {
    if (!viewSnapshot) return;
    setViewRestoring(true);
    try {
      await restoreSnapshot(viewSnapshot.id);
      setViewOpen(false);
      setViewSnapshot(null);
      if (onAfterRestore) await onAfterRestore();
      onClose();
    } catch (err) {
      setViewError(err.message || "Failed to restore this version.");
    } finally {
      setViewRestoring(false);
    }
  };

  // Restore one section from the preview. Pending local edits are flushed
  // first so they aren't lost, then the device pulls the restored records.
  const handleRestoreSection = async (section) => {
    if (!viewSnapshot) throw new Error("No version selected.");
    if (onFlushPending) await onFlushPending();
    const r = await restoreSnapshotSection(viewSnapshot.id, section);
    if (onAfterSectionRestore) await onAfterSectionRestore();
    loadSnapshots();
    return r;
  };

  const handleRestore = async (snapshotId) => {
    setRestoringId(snapshotId);
    setRestoreResult(null);
    try {
      await restoreSnapshot(snapshotId);
      setRestoreResult({ success: true, message: "Version restored." });
      setConfirmRestoreId(null);
      if (onAfterRestore) await onAfterRestore();
      onClose();
    } catch (err) {
      setRestoreResult({
        success: false,
        message: err.message || "Failed to restore this version.",
      });
    } finally {
      setRestoringId(null);
    }
  };

  const describeSnapshot = (s) => {
    const c = s.summary || {};
    const parts = [];
    if (c.conferences) parts.push(`${c.conferences} conf`);
    if (c.tabs) parts.push(`${c.tabs} tabs`);
    if (c.goals) parts.push(`${c.goals} goals`);
    if (c.clipboard) parts.push(`${c.clipboard} clips`);
    if (c.quickLinks) parts.push(`${c.quickLinks} links`);
    if (c.remember) parts.push(`${c.remember} notes`);
    if (c.days) parts.push(`${c.days} days`);
    return parts.length ? parts.join(" · ") : "empty";
  };

  const backdrop = useBackdropClose(
    onClose,
    !restoringId && !viewRestoring && !clearing,
  );

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(localSettings);
    onClose();
  };

  const handleReset = () => {
    onReset();
    setShowResetConfirm(false);
    onClose();
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = JSON.parse(event.target.result);
          onImport(data);
          onClose();
        } catch (error) {
          alert("Invalid file format. Please upload a valid JSON file.");
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      {...backdrop}
    >
      <div className="bg-gray-800 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-cyan-500/30 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-2xl font-bold text-yellow-400">
            Challenge Settings
          </h2>
          <button
            onClick={onClose}
            className="text-cyan-300 hover:text-cyan-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* User Name */}
          <div>
            <label className="block text-cyan-300 text-sm font-semibold mb-2">
              Your Name
            </label>
            <div className="relative">
              <input
                type="text"
                value={localSettings?.userName || settings?.userName || ""}
                readOnly
                className="w-full bg-gray-700/50 text-cyan-200 px-4 py-3 rounded-2xl border-2 border-gray-600 outline-none cursor-not-allowed opacity-80"
                placeholder="Not set"
              />
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Set during login (read-only)
            </p>
          </div>

          {/* Passkey: change it, keep recovery codes (cloud accounts only) */}
          {cloudEnabled ? (
            <PasskeySection isOpen={isOpen} />
          ) : (
            <div>
              <label className="block text-cyan-300 text-sm font-semibold mb-2">
                Passkey
              </label>
              <p className="text-xs text-gray-400">
                Local mode has no passkey — sign in with a Pro (cloud) account
                to protect your data with one.
              </p>
            </div>
          )}

          {/* Appearance */}
          <div className="space-y-4 border-t border-gray-700 pt-6">
            <h3 className="text-yellow-400 font-semibold flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Appearance
            </h3>

            {/* Theme */}
            <div>
              <label className="block text-cyan-300 text-sm font-semibold mb-2">
                Theme
              </label>
              <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Theme">
                {THEME_OPTIONS.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={theme === id}
                    onClick={() => setTheme(id)}
                    className={`tap flex items-center justify-center gap-2 px-3 py-3 rounded-2xl border-2 text-sm font-semibold transition-all ${
                      theme === id
                        ? "bg-cyan-500/20 border-cyan-500 text-cyan-200"
                        : "bg-gray-700/50 border-gray-600 text-gray-300 hover:border-cyan-500/50"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                System follows your device setting. Your choice is saved to your account.
              </p>
            </div>

            {/* Skin */}
            <div>
              <label className="block text-cyan-300 text-sm font-semibold mb-2">
                Skin
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Skin">
                {skins.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="radio"
                    aria-checked={skin === s.id}
                    onClick={() => setSkin(s.id)}
                    className={`text-left p-3 rounded-2xl border-2 transition-all ${
                      skin === s.id
                        ? "border-cyan-500 bg-cyan-500/10"
                        : "border-gray-600 bg-gray-700/40 hover:border-cyan-500/50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-cyan-200 font-semibold text-sm">{s.name}</span>
                      <span className="flex gap-1" aria-hidden="true">
                        {[s.preview.bg, s.preview.a, s.preview.b].map((c, i) => (
                          <span
                            key={i}
                            className="w-3.5 h-3.5 rounded-full border border-white/20"
                            style={{ background: c }}
                          />
                        ))}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 mt-1">{s.tagline}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Ambient effects */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-cyan-300 text-sm font-semibold flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Ambient effects
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  A subtle animated aurora behind the page. Desktop only; turns itself off
                  when your system prefers reduced motion.
                  {!ambientAvailable && " (Not available on this device.)"}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={ambientEffects}
                aria-label="Ambient effects"
                onClick={() => setAmbientEffects(!ambientEffects)}
                className={`relative flex-shrink-0 w-12 h-7 rounded-full transition-colors ${
                  ambientEffects ? "bg-cyan-500" : "bg-gray-600"
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    ambientEffects ? "translate-x-5" : ""
                  }`}
                />
              </button>
            </div>

            {/* Navigation rail side */}
            {onRailSideChange && (
              <div>
                <label className="block text-cyan-300 text-sm font-semibold mb-2">
                  Panel navigator
                </label>
                <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Panel navigator side">
                  {[
                    { id: "left", label: "Left side" },
                    { id: "right", label: "Right side" },
                  ].map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={railSide === id}
                      onClick={() => onRailSideChange(id)}
                      className={`tap px-3 py-2.5 rounded-2xl border-2 text-sm font-semibold transition-all ${
                        railSide === id
                          ? "bg-cyan-500/20 border-cyan-500 text-cyan-200"
                          : "bg-gray-700/50 border-gray-600 text-gray-300 hover:border-cyan-500/50"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Where the scroll-spy panel list (and its back-to-top button) sits.
                </p>
              </div>
            )}
          </div>

          {/* Streak reminder */}
          {onStreakReminderHourChange && (
            <div className="space-y-2 border-t border-gray-700 pt-6">
              <h3 className="text-yellow-400 font-semibold flex items-center gap-2">
                <BellRing className="w-5 h-5" />
                Streak reminder
              </h3>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <p className="text-xs text-gray-400 flex-1">
                  If today isn't marked by this time, a "streak at risk" banner
                  appears (and the sidebar shows a hint). Saved to your account.
                </p>
                <select
                  value={streakReminderHour}
                  onChange={(e) => onStreakReminderHourChange(Number(e.target.value))}
                  className="bg-gray-700 text-cyan-200 px-3 py-2 rounded-xl border-2 border-gray-600 focus:border-cyan-500 outline-none text-sm"
                  aria-label="Streak reminder time"
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>
                      {String(h).padStart(2, "0")}:00
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Challenge Name */}
          <div>
            <label className="block text-cyan-300 text-sm font-semibold mb-2">
              Challenge Name
            </label>
            <input
              type="text"
              value={localSettings.challengeName}
              onChange={(e) =>
                setLocalSettings({
                  ...localSettings,
                  challengeName: e.target.value,
                })
              }
              className="w-full bg-gray-700 text-cyan-200 px-4 py-3 rounded-2xl border-2 border-gray-600 focus:border-cyan-500 outline-none transition-colors"
              placeholder="My Awesome Challenge"
            />
          </div>

          {/* Number of Days */}
          <div>
            <label className="block text-cyan-300 text-sm font-semibold mb-2 flex items-center gap-2">
              <Hash className="w-4 h-4" />
              Number of Days
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={localSettings.numberOfDays}
              onChange={(e) =>
                setLocalSettings({
                  ...localSettings,
                  numberOfDays: parseInt(e.target.value) || 1,
                })
              }
              className="w-full bg-gray-700 text-cyan-200 px-4 py-3 rounded-2xl border-2 border-gray-600 focus:border-cyan-500 outline-none transition-colors"
            />
            <p className="text-xs text-gray-400 mt-2">
              Choose between 1 and 365 days for your challenge
            </p>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-cyan-300 text-sm font-semibold mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Start Date
            </label>
            <input
              type="date"
              value={localSettings.startDate}
              onChange={(e) =>
                setLocalSettings({
                  ...localSettings,
                  startDate: e.target.value,
                })
              }
              className="w-full bg-gray-700 text-cyan-200 px-4 py-3 rounded-2xl border-2 border-gray-600 focus:border-cyan-500 outline-none transition-colors"
            />
          </div>

          {/* Optional Panels */}
          {onToggleConferences && (
            <div className="border-t border-gray-700 pt-6">
              <h3 className="text-yellow-400 font-semibold mb-3">Panels</h3>
              <button
                onClick={() => onToggleConferences(!showConferences)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border-2 transition-all ${
                  showConferences
                    ? "bg-amber-500/15 border-amber-500/50 text-amber-300"
                    : "bg-gray-700 border-gray-600 text-gray-300 hover:border-amber-500/40"
                }`}
              >
                <span className="flex items-center gap-2 font-semibold">
                  <GraduationCap className="w-5 h-5" />
                  Conferences panel
                </span>
                <span
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    showConferences ? "bg-amber-500" : "bg-gray-600"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      showConferences ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </span>
              </button>
              <p className="text-xs text-gray-400 mt-2">
                Track conference submissions, deadlines and decisions. Off by
                default; your conference data is kept even when hidden.
              </p>

              {onTogglePublications && (
                <>
                  <button
                    onClick={() => onTogglePublications(!showPublications)}
                    className={`mt-4 w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border-2 transition-all ${
                      showPublications
                        ? "bg-purple-500/15 border-purple-500/50 text-purple-300"
                        : "bg-gray-700 border-gray-600 text-gray-300 hover:border-purple-500/40"
                    }`}
                  >
                    <span className="flex items-center gap-2 font-semibold">
                      <BookOpen className="w-5 h-5" />
                      Publications panel
                    </span>
                    <span
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                        showPublications ? "bg-purple-500" : "bg-gray-600"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          showPublications ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </span>
                  </button>
                  <p className="text-xs text-gray-400 mt-2">
                    Track your papers across conferences and journals — status,
                    dates, decisions and past attempts. Off by default; your
                    publication data is kept even when hidden.
                  </p>
                </>
              )}
            </div>
          )}

          {/* Logout Button */}
          {onLogout && (
            <div className="border-t border-gray-700 pt-6">
              <button
                onClick={() => {
                  onLogout();
                  onClose();
                }}
                className="w-full bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 px-4 py-3 rounded-2xl font-semibold transition-all border-2 border-orange-500/30 hover:border-orange-500 flex items-center justify-center gap-2"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
              <p className="text-xs text-gray-400 mt-2 text-center">
                You will need to login again to access your data
              </p>
            </div>
          )}

          {/* Export/Import */}
          <div className="space-y-3 border-t border-gray-700 pt-6">
            <h3 className="text-yellow-400 font-semibold">Data Management</h3>
            <div className="flex gap-3">
              <button
                onClick={onExport}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-cyan-300 px-4 py-3 rounded-2xl font-semibold transition-all border-2 border-cyan-500/30 hover:border-cyan-500 flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                Export Data
              </button>
              <label className="flex-1 bg-gray-700 hover:bg-gray-600 text-yellow-300 px-4 py-3 rounded-2xl font-semibold transition-all border-2 border-yellow-500/30 hover:border-yellow-500 flex items-center justify-center gap-2 cursor-pointer">
                <Upload className="w-5 h-5" />
                Import Data
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  className="hidden"
                />
              </label>
            </div>
            {(onExportMarkdown || onExportCsv || onShareCard) && (
              <div className="grid grid-cols-3 gap-2">
                {onExportMarkdown && (
                  <button
                    type="button"
                    onClick={onExportMarkdown}
                    className="tap bg-gray-700 hover:bg-gray-600 text-cyan-300 px-3 py-2.5 rounded-2xl text-sm font-semibold border-2 border-gray-600 hover:border-cyan-500 flex items-center justify-center gap-1.5"
                    title="Readable Markdown of everything on the page"
                  >
                    <FileText className="w-4 h-4" />
                    Markdown
                  </button>
                )}
                {onExportCsv && (
                  <button
                    type="button"
                    onClick={onExportCsv}
                    className="tap bg-gray-700 hover:bg-gray-600 text-cyan-300 px-3 py-2.5 rounded-2xl text-sm font-semibold border-2 border-gray-600 hover:border-cyan-500 flex items-center justify-center gap-1.5"
                    title="Goals, sub-goals, notes and days as a spreadsheet"
                  >
                    <Table2 className="w-4 h-4" />
                    CSV
                  </button>
                )}
                {onShareCard && (
                  <button
                    type="button"
                    onClick={onShareCard}
                    className="tap bg-gray-700 hover:bg-gray-600 text-yellow-300 px-3 py-2.5 rounded-2xl text-sm font-semibold border-2 border-gray-600 hover:border-yellow-500 flex items-center justify-center gap-1.5"
                    title="Download a shareable progress image (PNG)"
                  >
                    <Share2 className="w-4 h-4" />
                    Share card
                  </button>
                )}
              </div>
            )}

            {/* Cleanup Duplicates */}
            {onCleanupDuplicates && (
              <div className="mt-3">
                <button
                  onClick={async () => {
                    setIsCleaning(true);
                    setCleanupResult(null);
                    try {
                      const result = await onCleanupDuplicates();
                      setCleanupResult(result);
                    } catch (error) {
                      setCleanupResult({
                        success: false,
                        message:
                          error.message || "Failed to cleanup duplicates",
                      });
                    } finally {
                      setIsCleaning(false);
                    }
                  }}
                  disabled={isCleaning}
                  className={`w-full px-4 py-3 rounded-2xl font-semibold transition-all border-2 flex items-center justify-center gap-2 ${
                    isCleaning
                      ? "bg-gray-700/50 text-gray-500 border-gray-600 cursor-not-allowed"
                      : "bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 border-purple-500/30 hover:border-purple-500"
                  }`}
                >
                  <Trash2 className="w-5 h-5" />
                  {isCleaning
                    ? "Cleaning Duplicates..."
                    : "Remove Duplicate Entries"}
                </button>
                {cleanupResult && (
                  <div
                    className={`mt-2 p-3 rounded-xl border-2 ${
                      cleanupResult.success
                        ? "bg-green-600/10 border-green-500/30 text-green-300"
                        : "bg-red-600/10 border-red-500/30 text-red-300"
                    }`}
                  >
                    <p className="text-sm font-semibold">
                      {cleanupResult.message}
                    </p>
                    {cleanupResult.results && (
                      <div className="mt-2 text-xs space-y-1">
                        {Object.entries(cleanupResult.results).map(
                          ([sheet, stats]) =>
                            stats.removed > 0 && (
                              <p key={sheet}>
                                {sheet}: Removed {stats.removed}, Kept{" "}
                                {stats.kept}
                              </p>
                            ),
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* AI companion (cloud users only — keys live encrypted in the cloud) */}
          {cloudEnabled && (
            <AiSettingsSection isOpen={isOpen} onChanged={onAiSettingsChanged} />
          )}

          {/* Version History (cloud users only) */}
          {cloudEnabled && (
            <div className="space-y-3 border-t border-gray-700 pt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-yellow-400 font-semibold flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Version History
                </h3>
                <button
                  onClick={loadSnapshots}
                  disabled={loadingSnapshots}
                  className="text-xs text-cyan-400 hover:text-cyan-300 disabled:text-gray-500"
                >
                  {loadingSnapshots ? "Loading…" : "Refresh"}
                </button>
              </div>
              <p className="text-xs text-gray-400">
                A version of all your data is saved on the schedule below (and
                always right before a restore). Restore one to roll back — your
                current data is saved first, so a restore is itself undoable.
              </p>

              {snapPrefs && (
                <div className="rounded-2xl bg-gray-700/40 border border-gray-600 p-3 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <label className="text-xs text-gray-300 space-y-1">
                      <span className="block text-cyan-300 font-semibold">Save a version every</span>
                      <select
                        value={snapPrefs.everyDays}
                        disabled={snapPrefsBusy}
                        onChange={(e) => updateSnapPrefs({ everyDays: Number(e.target.value) })}
                        className="w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-xl border-2 border-gray-600 focus:border-cyan-500 outline-none"
                      >
                        {SNAPSHOT_EVERY_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n === 1 ? "day" : `${n} days`}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs text-gray-300 space-y-1">
                      <span className="block text-cyan-300 font-semibold">At (local time)</span>
                      <select
                        value={snapPrefs.hour}
                        disabled={snapPrefsBusy}
                        onChange={(e) => updateSnapPrefs({ hour: Number(e.target.value) })}
                        className="w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-xl border-2 border-gray-600 focus:border-cyan-500 outline-none"
                      >
                        {Array.from({ length: 24 }, (_, h) => (
                          <option key={h} value={h}>
                            {HOUR_LABEL(h)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs text-gray-300 space-y-1">
                      <span className="block text-cyan-300 font-semibold">Keep at most</span>
                      <select
                        value={snapPrefs.max}
                        disabled={snapPrefsBusy}
                        onChange={(e) => updateSnapPrefs({ max: Number(e.target.value) })}
                        className="w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-xl border-2 border-gray-600 focus:border-cyan-500 outline-none"
                      >
                        {SNAPSHOT_MAX_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n} versions
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <p className="text-[11px] text-gray-500">
                    Time zone: {snapPrefs.tz || "UTC"}. A scheduled version is
                    skipped when nothing changed since the newest one.
                    {snapPrefs.lastAutoDay ? ` Last scheduled save: ${snapPrefs.lastAutoDay}.` : ""}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleSaveNow}
                      disabled={savingNow}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30 border border-cyan-500/40 disabled:opacity-60 flex items-center gap-1.5"
                    >
                      <Save className="w-3.5 h-3.5" />
                      {savingNow ? "Saving…" : "Save a version now"}
                    </button>
                    {confirmClear ? (
                      <>
                        <button
                          type="button"
                          onClick={handleClearAll}
                          disabled={clearing}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-500 disabled:opacity-60"
                        >
                          {clearing ? "Clearing…" : "Yes, delete all versions"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmClear(false)}
                          disabled={clearing}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-600 text-gray-200 hover:bg-gray-500"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmClear(true)}
                        disabled={snapshots.length === 0}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600/10 text-red-300 hover:bg-red-600/20 border border-red-500/30 disabled:opacity-40 flex items-center gap-1.5"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Clear all versions
                      </button>
                    )}
                  </div>
                  {snapNotice && (
                    <div
                      className={`p-2.5 rounded-xl border text-xs ${
                        snapNotice.ok
                          ? "bg-green-600/10 border-green-500/30 text-green-300"
                          : "bg-red-600/10 border-red-500/30 text-red-300"
                      }`}
                    >
                      {snapNotice.text}
                    </div>
                  )}
                </div>
              )}

              {snapshotError && (
                <div className="p-3 rounded-xl border-2 bg-red-600/10 border-red-500/30 text-red-300 text-sm">
                  {snapshotError}
                </div>
              )}

              {!snapshotError &&
                !loadingSnapshots &&
                snapshots.length === 0 && (
                  <p className="text-sm text-gray-500">
                    No saved versions yet.
                  </p>
                )}

              {snapshots.length > 0 && (
                <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                  {snapshots.map((s) => (
                    <div
                      key={s.id}
                      className="bg-gray-700/50 rounded-2xl border border-gray-600 p-3"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 text-cyan-200 text-sm">
                            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">
                              {formatTimestampFull(s.takenAt)}
                            </span>
                            {s.reason === "before-restore" && (
                              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-[10px] uppercase tracking-wide">
                                pre-restore
                              </span>
                            )}
                            {s.reason === "manual" && (
                              <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded text-[10px] uppercase tracking-wide">
                                manual
                              </span>
                            )}
                            {s.reason === "scheduled" && (
                              <span className="px-2 py-0.5 bg-gray-600/60 text-gray-300 rounded text-[10px] uppercase tracking-wide">
                                scheduled
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mt-1 truncate">
                            {describeSnapshot(s)}
                          </div>
                        </div>
                        {confirmRestoreId === s.id ? (
                          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                            <span className="text-[11px] text-yellow-300 max-w-[11rem] text-right">
                              {confirmRestoreStep === 1
                                ? "Replace all current data? (1 of 2)"
                                : "Sure? Changes since then are overwritten. (2 of 2)"}
                            </span>
                            <button
                              onClick={() =>
                                confirmRestoreStep === 1
                                  ? setConfirmRestoreStep(2)
                                  : handleRestore(s.id)
                              }
                              disabled={restoringId === s.id}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-yellow-500 text-gray-900 hover:bg-yellow-400 disabled:opacity-60"
                            >
                              {restoringId === s.id
                                ? "Restoring…"
                                : confirmRestoreStep === 1
                                  ? "Continue"
                                  : "Yes, restore"}
                            </button>
                            <button
                              onClick={() => setConfirmRestoreId(null)}
                              disabled={restoringId === s.id}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-600 text-gray-200 hover:bg-gray-500"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-2 flex-shrink-0">
                            <button
                              onClick={() => handleView(s.id)}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-600/60 text-cyan-300 hover:bg-gray-600 border border-cyan-500/30 hover:border-cyan-500 flex items-center gap-1.5"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              View
                            </button>
                            <button
                              onClick={() => {
                                setRestoreResult(null);
                                setConfirmRestoreStep(1);
                                setConfirmRestoreId(s.id);
                              }}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-600/60 text-cyan-300 hover:bg-gray-600 border border-cyan-500/30 hover:border-cyan-500 flex items-center gap-1.5"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              Restore
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {restoreResult && (
                <div
                  className={`p-3 rounded-xl border-2 text-sm ${
                    restoreResult.success
                      ? "bg-green-600/10 border-green-500/30 text-green-300"
                      : "bg-red-600/10 border-red-500/30 text-red-300"
                  }`}
                >
                  {restoreResult.message}
                </div>
              )}
            </div>
          )}

          {/* Read-only preview of a selected version */}
          <SnapshotViewerModal
            isOpen={viewOpen}
            snapshot={viewSnapshot}
            loading={viewLoading}
            error={viewError}
            restoring={viewRestoring}
            onClose={() => setViewOpen(false)}
            onRestore={handleRestoreFromView}
            onRestoreSection={cloudEnabled ? handleRestoreSection : undefined}
          />

          {/* Reset Section */}
          <div className="border-t border-gray-700 pt-6">
            <h3 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </h3>

            {/* Delete All Clipboards */}
            {onDeleteAllClipboards && (
              <div className="mb-4">
                <button
                  onClick={onDeleteAllClipboards}
                  disabled={clipboardCount === 0}
                  className={`w-full px-4 py-3 rounded-2xl font-semibold transition-all border-2 flex items-center justify-center gap-2 ${
                    clipboardCount === 0
                      ? "bg-gray-700/50 text-gray-500 border-gray-600 cursor-not-allowed"
                      : "bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 border-orange-500/30 hover:border-orange-500"
                  }`}
                >
                  <Clipboard className="w-5 h-5" />
                  Delete All Clipboard Cards ({clipboardCount})
                </button>
              </div>
            )}

            {/* Reset All Data */}
            {!showResetConfirm ? (
              <button
                onClick={() => setShowResetConfirm(true)}
                className="w-full bg-red-600/20 hover:bg-red-600/30 text-red-400 px-4 py-3 rounded-2xl font-semibold transition-all border-2 border-red-500/30 hover:border-red-500"
              >
                Reset All Data
              </button>
            ) : (
              <div className="space-y-3 p-3 bg-red-600/10 rounded-2xl border-2 border-red-500/30">
                <p className="text-red-300 text-sm">
                  Are you sure? This will delete all your progress, goals, and
                  settings. This action cannot be undone!
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleReset}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl font-semibold transition-all"
                  >
                    Yes, Reset Everything
                  </button>
                  <button
                    onClick={() => setShowResetConfirm(false)}
                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-cyan-300 px-4 py-2 rounded-xl font-semibold transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-cyan-300 px-6 py-3 rounded-2xl font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 bg-gradient-to-r from-yellow-400 to-cyan-400 hover:from-yellow-300 hover:to-cyan-300 text-black px-6 py-3 rounded-2xl font-semibold transition-all flex items-center justify-center gap-2"
          >
            <Save className="w-5 h-5" />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
